import React, { type FC, useCallback, useMemo } from 'react';
import { IS_MY_TEAM } from 'reactApp/appHelpers/configHelpers';
import { duplicatesLimitCount } from 'reactApp/appHelpers/featuresHelpers/features/duplicates';
import { removeDuplicateFromViewerStart } from 'reactApp/modules/duplicates/duplicates.actions';
import type { IDuplicateItem } from 'reactApp/modules/duplicates/duplicates.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { WorkspaceSuperAppService } from 'reactApp/services';
import { store as reduxStore } from 'reactApp/store';
import {
    getDuplicatesRemovedGroupsCount,
    renderDuplicatesRemoveLimitModal,
} from 'reactApp/ui/Duplicates/DuplicatesRemoveLimitModal/DuplicatesRemoveLimitModal.helpers';
import { CloseMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/CloseMenuButton/CloseMenuButton';
import { DownloadMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/DownloadMenuButton/DownloadMenuButton';
import { MenuInfo } from 'reactApp/ui/ReactViewer/ViewerMenu/MenuInfo/MenuInfo';
import { MenuLogo } from 'reactApp/ui/ReactViewer/ViewerMenu/MenuLogo/MenuLogo';
import { MoreMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton';
import { MenuItemName } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton.types';
import type { ViewerMenuProps } from 'reactApp/ui/ReactViewer/ViewerMenu/ViewerMenu';
import { ViewerMenuCenter, ViewerMenuEnd, ViewerMenuStart, ViewerMenuWrapper } from 'reactApp/ui/ReactViewer/ViewerMenu/ViewerMenu.blocks';
import { Zoomer } from 'reactApp/ui/ReactViewer/ViewerMenu/Zoomer/Zoomer';
import { sendXray } from 'reactApp/utils/ga';
import opener from 'reactApp/utils/opener';
import { openLink } from 'reactApp/utils/openHelpers';
import { getPathWithoutFile } from 'reactApp/utils/urlHelper';

interface Props extends ViewerMenuProps {
    file: IDuplicateItem;
}

export const DuplicatesMenu: FC<Props> = ({
    file,
    showLogo,
    zoom,
    decreaseZoom,
    increaseZoom,
    showDownload,
    onDownload,
    showClose,
    onClose,
}) => {
    const onOpenParent = useCallback((file: IDuplicateItem) => {
        const fileName = encodeURIComponent(file.path);
        const pathWithoutFile = encodeURIComponent(getPathWithoutFile(file.path));

        sendXray('click', 'open-parent');

        const isWorkspaceSuperAppMode = WorkspaceSuperAppService.isEnabled;

        let link = `${window.location.origin}/home${pathWithoutFile}/?item=${fileName}&action=scroll-to`;
        if (isWorkspaceSuperAppMode) {
            link = `${WorkspaceSuperAppService.superAppOrigin.origin}/disk/home${pathWithoutFile}/?item=${fileName}&action=scroll-to`;
            openLink(link, '_blank');
            return;
        }

        opener(link, IS_MY_TEAM);
    }, []);

    const moreItems = useMemo(
        () => [
            {
                name: MenuItemName.OPEN_IN_PARENT,
                onClick: () => {
                    onOpenParent(file);
                },
                divider: true,
            },
            {
                name: MenuItemName.DELETE,
                onClick: () => {
                    const state = reduxStore.getState();
                    const isCorp = UserSelectors.isCorpUser(state);
                    const isPaid = UserSelectors.isPaidUser(state);
                    const duplicatesLimitEnabled = !isCorp && !isPaid;
                    const removedGroupsCount = Number(reduxStore.dispatch(getDuplicatesRemovedGroupsCount())) || 0;
                    if (duplicatesLimitEnabled && removedGroupsCount >= duplicatesLimitCount) {
                        // Лимит исчерпан
                        onClose(file);
                        renderDuplicatesRemoveLimitModal();
                    } else {
                        reduxStore.dispatch(removeDuplicateFromViewerStart({ item: file }));
                    }
                },
            },
        ],
        [file, onClose, onOpenParent]
    );

    return (
        <ViewerMenuWrapper>
            <ViewerMenuStart>
                {showLogo && <MenuLogo />}
                <MenuInfo file={file} />
            </ViewerMenuStart>
            <ViewerMenuCenter>{zoom && <Zoomer value={zoom} decrease={decreaseZoom} increase={increaseZoom} />}</ViewerMenuCenter>
            <ViewerMenuEnd>
                {showDownload && (
                    <DownloadMenuButton
                        file={file}
                        onClick={() => {
                            onDownload(file);
                        }}
                    />
                )}
                <MoreMenuButton list={moreItems} />
                {showClose && (
                    <CloseMenuButton
                        onClick={() => {
                            onClose(file);
                        }}
                    />
                )}
            </ViewerMenuEnd>
        </ViewerMenuWrapper>
    );
};
